import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../components/Common/Header';
import CustomerModal from '../components/Customers/CustomerModal';
import { exportCustomersExcel } from '../utils/customerExcelExport';
// 需求 R117（PM 2026-08-18 拍板 Q8=A／Q7=A；R3 PM 追加「四頁排列對齊客戶管理」）：
// 本頁原本的分頁列（每頁顯示 → 頁碼 → 筆數資訊）已抽成共用元件，四頁一起 import。
import Pagination from '../components/Common/Pagination';
import './CustomersPage.css';

const API = process.env.REACT_APP_API_URL || '/api';

const CustomersPage = () => {
  const navigate = useNavigate();
  const [currentUser, setCurrentUser] = useState(null);
  const [customers, setCustomers] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);

  // filters
  const [keyword, setKeyword] = useState('');
  const [source, setSource] = useState('');
  const [dealFilter, setDealFilter] = useState('');
  const [levelFilter, setLevelFilter] = useState('');
  // 需求 R21：以「負責業務」下拉取代原本的「客戶國別」文字篩選。
  const [assignedSalesFilter, setAssignedSalesFilter] = useState('');
  const [salesOptions, setSalesOptions] = useState([]);

  // modal
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState('add'); // add | view | edit
  const [selectedCustomer, setSelectedCustomer] = useState(null);

  // 需求 R22：跨分頁選取以 Set 維護 _id。
  // PM（2026-05-18）確認：篩選條件變更時自動清除全部已選，
  // 切換分頁 / 變更每頁筆數 / 變更排序時不清空。
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [exporting, setExporting] = useState(false);
  const [selectingAllMatching, setSelectingAllMatching] = useState(false);
  const isManager = currentUser?.role === 'manager';

  useEffect(() => {
    const token = localStorage.getItem('token');
    const user = JSON.parse(localStorage.getItem('user') || '{}');
    if (!token || !user._id) { navigate('/login'); return; }
    setCurrentUser(user);
  }, [navigate]);

  // 需求 R22：把目前 filter 條件序列化，給 fetch list / fetch all-ids 共用。
  const buildFilterParams = useCallback(() => {
    const params = new URLSearchParams();
    if (keyword) params.set('keyword', keyword);
    if (source) params.set('source', source);
    if (dealFilter) params.set('deal', dealFilter);
    if (levelFilter) params.set('level', levelFilter);
    if (assignedSalesFilter) params.set('assignedSalesId', assignedSalesFilter);
    return params;
  }, [keyword, source, dealFilter, levelFilter, assignedSalesFilter]);

  const fetchCustomers = useCallback(async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    const params = buildFilterParams();
    params.set('page', page);
    params.set('limit', limit);
    try {
      const res = await fetch(`${API}/customers?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) {
        const data = await res.json();
        setCustomers(data.customers);
        setTotal(data.total);
      }
    } catch (err) {
      console.error('載入客戶失敗', err);
    }
  }, [page, limit, buildFilterParams]);

  useEffect(() => { if (currentUser) fetchCustomers(); }, [currentUser, fetchCustomers]);

  // 需求 R22：filter 條件變更時自動清空已選（PM 確認結論）。page/limit 變更則不清。
  // 用 ref 記錄「上一次的 filter signature」，與目前比較。首次掛載不觸發。
  const filterSig = useMemo(
    () => JSON.stringify({ keyword, source, dealFilter, levelFilter, assignedSalesFilter }),
    [keyword, source, dealFilter, levelFilter, assignedSalesFilter]
  );
  const lastFilterSig = useRef(filterSig);
  useEffect(() => {
    if (lastFilterSig.current !== filterSig) {
      lastFilterSig.current = filterSig;
      setSelectedIds(new Set());
    }
  }, [filterSig]);

  // 需求 R21：載入負責業務下拉選項（role ∈ {sales, manager} 且 isActive）。
  useEffect(() => {
    if (!currentUser) return;
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API}/users`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => (res.ok ? res.json() : []))
      .then((users) => {
        const list = Array.isArray(users) ? users : [];
        setSalesOptions(list.filter((u) => u.isActive && (u.role === 'sales' || u.role === 'manager')));
      })
      .catch(() => setSalesOptions([]));
  }, [currentUser]);

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    navigate('/login');
  };

  // Modal handlers
  const openAdd = () => { setSelectedCustomer(null); setModalMode('add'); setModalOpen(true); };
  const openView = (c) => { setSelectedCustomer(c); setModalMode('view'); setModalOpen(true); };
  const closeModal = () => { setModalOpen(false); setSelectedCustomer(null); };

  const handleSave = async (formData) => {
    const token = localStorage.getItem('token');
    const isEdit = modalMode === 'edit' && selectedCustomer;
    const url = isEdit ? `${API}/customers/${selectedCustomer._id}` : `${API}/customers`;
    const method = isEdit ? 'PUT' : 'POST';
    try {
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify(formData),
      });
      if (res.ok) { closeModal(); fetchCustomers(); return; }
      const data = await res.json().catch(() => ({}));
      // 需求 R21：客戶編號 unique index 衝突時後端回傳 409 + DUPLICATE_CUSTOMER_NUMBER。
      if (res.status === 409 && data.code === 'DUPLICATE_CUSTOMER_NUMBER') {
        alert(data.message || '客戶編號已存在，請使用其他編號');
        return;
      }
      alert(data.message || '儲存失敗');
    } catch (err) {
      console.error('儲存失敗', err);
    }
  };

  const handleDelete = async (id) => {
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/customers/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) { closeModal(); fetchCustomers(); }
    } catch (err) {
      console.error('刪除失敗', err);
    }
  };

  const handleBatchDelete = async () => {
    if (selectedIds.size === 0) return;
    if (!window.confirm(`確定要刪除 ${selectedIds.size} 筆客戶？`)) return;
    const token = localStorage.getItem('token');
    try {
      const res = await fetch(`${API}/customers/batch-delete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids: Array.from(selectedIds) }),
      });
      if (res.ok) { setSelectedIds(new Set()); fetchCustomers(); }
    } catch (err) {
      console.error('批量刪除失敗', err);
    }
  };

  // 需求 R22：選取操作（以 Set 不可變更新）。
  const toggleSelect = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const pageAllSelected = customers.length > 0 && customers.every((c) => selectedIds.has(c._id));
  const togglePageSelectAll = () => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (pageAllSelected) {
        customers.forEach((c) => next.delete(c._id));
      } else {
        customers.forEach((c) => next.add(c._id));
      }
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  // 需求 R22：跨分頁全選 — 呼叫 GET /api/customers/all-ids 取回所有符合 filter 的 _id。
  const selectAllMatching = async () => {
    const token = localStorage.getItem('token');
    if (!token) return;
    setSelectingAllMatching(true);
    try {
      const params = buildFilterParams();
      const res = await fetch(`${API}/customers/all-ids?${params}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        alert('取得全部符合條件的客戶清單失敗');
        return;
      }
      const data = await res.json();
      const ids = data.ids || [];
      setSelectedIds((prev) => {
        const next = new Set(prev);
        ids.forEach((id) => next.add(id));
        return next;
      });
    } catch (err) {
      console.error('跨分頁全選失敗', err);
      alert('跨分頁全選失敗');
    } finally {
      setSelectingAllMatching(false);
    }
  };

  // 需求 R22：匯出 Excel — 後端只回傳資料來源，xlsx 由前端 ExcelJS 產出。
  const handleExport = async () => {
    if (selectedIds.size === 0 || exporting) return;
    const token = localStorage.getItem('token');
    setExporting(true);
    try {
      const res = await fetch(`${API}/customers/export`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ ids: Array.from(selectedIds) }),
      });
      if (!res.ok) {
        const data = await res.json().catch(() => ({}));
        alert(data.message || '匯出失敗');
        return;
      }
      const data = await res.json();
      await exportCustomersExcel({ customers: data.customers || [] });
      alert(`已匯出 ${data.customers?.length || 0} 筆客戶資料`);
    } catch (err) {
      console.error('匯出失敗', err);
      alert('匯出失敗，請稍後再試');
    } finally {
      setExporting(false);
    }
  };

  const totalPages = Math.ceil(total / limit) || 1;
  // 顯示區間（startIdx／endIdx）已改由 <Pagination> 依 page/pageSize/total 自行計算，
  // 四頁的「顯示第 X–Y 筆，共 N 筆」因此保證同一條公式、同一種文案（R3 對齊）。

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d).toLocaleDateString('zh-TW', { year: 'numeric', month: '2-digit', day: '2-digit' });
  };

  // 跨分頁全選提示：本頁全選且 total > customers.length 且尚未全部選滿時顯示
  const showSelectAllMatchingHint = isManager
    && pageAllSelected
    && total > customers.length
    && selectedIds.size < total;

  if (!currentUser) return null;

  return (
    <div className="customers-page">
      <Header
        title="👥 客戶管理"
        user={currentUser}
        onLogout={handleLogout}
        onUserUpdate={setCurrentUser}
      />

      <div className="container" style={{ maxWidth: 1400, margin: '0 auto', padding: 'var(--spacing-lg)' }}>
        {/* Filter */}
        <section className="customer-filter-section">
          <div className="filter-section-title">🔍 搜尋與篩選</div>
          <div className="filter-row">
            <div className="filter-group filter-group-wide">
              <label>關鍵字搜尋</label>
              <input type="text" className="form-control form-control-sm" placeholder="輸入客戶名稱、編號、簡稱、聯絡人、Email..." value={keyword} onChange={(e) => { setKeyword(e.target.value); setPage(1); }} />
            </div>
            <div className="filter-group">
              <label>負責業務</label>
              <select className="form-control form-control-sm" value={assignedSalesFilter} onChange={(e) => { setAssignedSalesFilter(e.target.value); setPage(1); }}>
                <option value="">全部</option>
                <option value="none">未指派</option>
                {salesOptions.map((u) => (
                  <option key={u._id} value={u._id}>{u.name}</option>
                ))}
              </select>
            </div>
            <div className="filter-group">
              <label>客戶來源</label>
              <input type="text" className="form-control form-control-sm" placeholder="輸入來源，如：展覽、官網..." value={source} onChange={(e) => { setSource(e.target.value); setPage(1); }} />
            </div>
          </div>
          <div className="filter-row">
            <div className="filter-group">
              <label>客戶級別</label>
              <div className="level-filter">
                {[
                  { key: '', label: '全部', cls: 'all' },
                  { key: 'A', label: 'A級', cls: 'a' },
                  { key: 'B', label: 'B級', cls: 'b' },
                  { key: 'C', label: 'C級', cls: 'c' },
                  { key: 'D', label: 'D級', cls: 'd' },
                  { key: 'none', label: '未分級', cls: 'none' },
                ].map((l) => (
                  <button key={l.key} className={`level-filter-btn ${l.cls}${levelFilter === l.key ? ' active' : ''}`} onClick={() => { setLevelFilter(l.key); setPage(1); }}>
                    {l.label}
                  </button>
                ))}
              </div>
            </div>
            <div className="filter-group">
              <label>成交狀態</label>
              <div className="deal-filter">
                {[
                  { key: '', label: '全部', cls: 'all' },
                  { key: 'yes', label: '✓ 已成交', cls: 'yes' },
                  { key: 'no', label: '— 未成交', cls: 'no' },
                ].map((d) => (
                  <button key={d.key} className={`deal-filter-btn ${d.cls}${dealFilter === d.key ? ' active' : ''}`} onClick={() => { setDealFilter(d.key); setPage(1); }}>
                    {d.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Action bar */}
        <section className="customer-action-bar">
          <div className="action-group">
            <button className="btn btn-sm btn-success" onClick={openAdd}>+ 新增客戶</button>
            {isManager && (
              <>
                <button className="btn btn-sm" style={{ backgroundColor: 'var(--color-danger)', color: 'var(--color-text-inverse)', border: '1px solid var(--color-danger-dark)' }} onClick={handleBatchDelete} disabled={!selectedIds.size}>
                  刪除選取客戶
                </button>
                {/* 需求 R22：匯出 Excel（主管權限）。
                    按鈕順序：位於「刪除選取客戶」之後（PM 2026-05-18 確認）。
                    樣式統一沿用全站「匯出」按鈕 btn-outline btn-sm，與型錄頁「匯出型錄」、
                    產品頁「匯出成本表」採完全相同的 className（文字色 / 框線色皆為 --color-primary-dark）；
                    未勾選任何客戶時觸發 .btn:disabled 全站樣式（opacity 0.6）使按鈕看起來較淺，
                    勾選後恢復與其他匯出按鈕一致的飽和橘色。 */}
                <button
                  className="btn btn-outline btn-sm"
                  onClick={handleExport}
                  disabled={!selectedIds.size || exporting}
                >
                  {exporting ? '匯出中...' : '匯出 Excel'}
                </button>
              </>
            )}
          </div>
          <div className="result-count">共 <strong>{total}</strong> 筆客戶</div>
        </section>

        {/* 需求 R22：選取狀態列（manager 才顯示，因匯出 / 批刪皆為主管專屬） */}
        {isManager && selectedIds.size > 0 && (
          <div className="customer-selection-bar">
            <div>
              已選取 <strong>{selectedIds.size}</strong> 筆 / 共 <strong>{total}</strong> 筆
            </div>
            <button className="link-btn" type="button" onClick={clearSelection}>清除選取</button>
          </div>
        )}
        {showSelectAllMatchingHint && (
          <div className="customer-selection-bar customer-selection-hint">
            <div>
              已選取本頁 {customers.length} 筆，
              <button className="link-btn" type="button" onClick={selectAllMatching} disabled={selectingAllMatching}>
                {selectingAllMatching ? '取得中…' : `點此選取符合條件的全部 ${total} 筆`}
              </button>
            </div>
          </div>
        )}

        {/* Table */}
        <div className="customer-table-container">
          <table className="customer-table">
            <thead>
              <tr>
                {isManager && (
                  <th style={{ width: 40, textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      className="select-checkbox"
                      checked={pageAllSelected}
                      onChange={togglePageSelectAll}
                    />
                  </th>
                )}
                <th>建檔日期</th>
                <th>客戶級別</th>
                <th>客戶編號</th>
                <th>客戶簡稱</th>
                <th>聯絡人</th>
                <th>Email</th>
                <th>負責業務</th>
                <th>客戶來源</th>
                <th style={{ textAlign: 'center' }}>成交</th>
                <th style={{ textAlign: 'center', minWidth: 80 }}>操作</th>
              </tr>
            </thead>
            <tbody>
              {customers.map((c) => (
                <tr key={c._id}>
                  {isManager && (
                    <td style={{ textAlign: 'center' }}>
                      <input type="checkbox" className="select-checkbox" checked={selectedIds.has(c._id)} onChange={() => toggleSelect(c._id)} />
                    </td>
                  )}
                  <td style={{ whiteSpace: 'nowrap' }}>{formatDate(c.createdAt)}</td>
                  <td>
                    {c.level ? (
                      <span className={`level-badge level-${c.level.toLowerCase()}`}>{c.level}級</span>
                    ) : (
                      <span className="level-badge level-none">—</span>
                    )}
                  </td>
                  <td>{c.customerNumber || '—'}</td>
                  <td>
                    {/* 顯示客戶簡稱；空值 → '—'。hover 顯示完整客戶名稱方便辨識。 */}
                    <div className="customer-name" title={c.name || ''}>{c.shortName || '—'}</div>
                  </td>
                  <td>{c.contacts?.[0]?.name || '—'}</td>
                  <td>{c.contacts?.[0]?.email || '—'}</td>
                  <td>{c.assignedSalesId?.name || '—'}</td>
                  <td>{c.source || '—'}</td>
                  <td style={{ textAlign: 'center' }}>
                    <span className={`status-badge ${c.deal ? 'status-deal-yes' : 'status-deal-no'}`}>
                      {c.deal ? '已成交' : '未成交'}
                    </span>
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <div className="row-actions">
                      <button className="row-action-btn btn-edit" onClick={() => openView(c)}>詳情</button>
                    </div>
                  </td>
                </tr>
              ))}
              {customers.length === 0 && (
                <tr><td colSpan={isManager ? 11 : 10} style={{ textAlign: 'center', padding: 40, color: 'var(--color-text-secondary)' }}>尚無客戶資料</td></tr>
              )}
            </tbody>
          </table>
        </div>

        {/*
          Pagination
          需求 R117 R3：PM 指定「四頁排列對齊客戶管理」，所以本頁這一套（每頁顯示 → 頁碼 → 筆數資訊）
          被原封不動抽成共用元件 components/Common/Pagination.js，本頁改成 import 它。
          排列、‹／›、省略號寫法（Q7=A(a) 的視窗演算法）、筆數文案都與原本逐字相同。
        */}
        <Pagination
          page={page}
          totalPages={totalPages}
          total={total}
          pageSize={limit}
          pageSizeOptions={[20, 50, 100]}
          onPageChange={setPage}
          onPageSizeChange={(n) => { setLimit(n); setPage(1); }}
        />
      </div>

      <CustomerModal
        open={modalOpen}
        mode={modalMode}
        customer={selectedCustomer}
        onClose={closeModal}
        onSave={handleSave}
        onDelete={handleDelete}
        onModeChange={setModalMode}
        currentUser={currentUser}
      />
    </div>
  );
};

export default CustomersPage;
